/**@format */
import supportsHTML5Storage from './supportsHTML5Storage';
import Time from '../components/Time';

/*
 * This is to abstract out cache as in future we might
 * use different tech than localstorage
 */

const setCache = (cache_key, data, options = {}) => {
  if (!supportsHTML5Storage()) {
    return;
  }
  const payload = {
    value: data,
    expires_on: options.expires_on || Time.add(new Date(), 7, 'day'), // when we consider this key expired
  };

  const payloadString = JSON.stringify(payload);
  const usagePercent = estimateLocalStorageUsage();
  // since the largest storage consumer of local storage is note / update draft, if the local storage is
  // found to be more than 75% we delete the expired or note / update drafts that are created 2 or more days ago
  if (usagePercent >= 75) {
    deleteExpiredNoteOrUpdateDraftCache();
  }
  localStorage.setItem(cache_key, payloadString);
};

/**
 * Estimates the local storage usage
 * @returns % of usage
 */
function estimateLocalStorageUsage() {
  const MAX_QUOTA = 5242880; // 5 MB in bytes (most browsers)
  let usedBytes = 0;

  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      const value = localStorage.getItem(key);

      // Skip null / undefined values
      if (value === null || value === undefined) {
        continue;
      }

      let valueLength = 0;
      valueLength =
        typeof value === 'string' ? value.length : String(value).length;

      usedBytes += (key.length + valueLength) * 2; // UTF-16 = 2 bytes/char
    }
  }

  const usagePercent = (usedBytes / MAX_QUOTA) * 100;
  return usagePercent;
}

/**
 * Removes the outdated notes or update draft if it has been added 2 or more days ago
 */
function deleteExpiredNoteOrUpdateDraftCache() {
  for (var i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    let cachedValue = localStorage.getItem(key);
    if (cachedValue && typeof cachedValue === 'string') {
      // perform the deletion for all note / update drafts only
      if (key.indexOf('note_draft') > -1 || key.indexOf('update_draft') > -1) {
        cachedValue = JSON.parse(cachedValue);
        // Since the expriry date of note / update draft is 7 days from creation, subtraction 7 days would give creation date
        const dateOfCreation = new Date(
          Time.subtract(new Date(cachedValue.expires_on), 7, 'day')
        );
        const dayBeforeYesterday = new Date(
          Time.endOf(Time.subtract(new Date(), 2, 'day'))
        );
        const dateOfExpiry = new Date(cachedValue.expires_on);
        // consider the cache as old and delete if
        // 1. Date of expiry is less than or equal to today
        // 2. Date of Creation is 2 or more days ago
        if (
          dateOfExpiry <= new Date() ||
          dateOfCreation <= dayBeforeYesterday
        ) {
          localStorage.removeItem(key);
        }
      }
    }
  }
}

const getCache = cache_key => {
  if (!supportsHTML5Storage()) {
    return;
  }
  let cachedValue = localStorage.getItem(cache_key);
  if (cachedValue && typeof cachedValue === 'string') {
    try {
      cachedValue = JSON.parse(cachedValue);
      // If expires_on is in the past, expire
      if (new Date(cachedValue.expires_on) < new Date()) {
        localStorage.removeItem(cache_key);
        return null;
      }
      // Return value if it has not expired
      return cachedValue.value;
    } catch (e) {
      return null;
    }
  }
  return null;
};

const removeCache = cache_key => {
  if (!supportsHTML5Storage()) {
    return;
  }
  localStorage.removeItem(cache_key);
};

// Delete all cache key-values which are expired
const deleteOrphanCaches = () => {
  if (!supportsHTML5Storage()) {
    return;
  }
  for (var i = 0; i < localStorage.length; i++) {
    let cachedValue = localStorage.getItem(localStorage.key(i));
    if (cachedValue && typeof cachedValue === 'string') {
      try {
        cachedValue = JSON.parse(cachedValue);
        // If expires_on is in the past, expire
        if (new Date(cachedValue.expires_on) < new Date()) {
          localStorage.removeItem(localStorage.key(i));
          return null;
        }
      } catch (e) {
        return null;
      }
    }
    return null;
  }
};

// TODO: Figure out the appropriate place in the structure for this
export const getOrganizationSettingCacheKey = key => {
  return `v1/ORGANIZATION_SETTINGS/${key}`;
};

export const getPortcoSettingCacheKey = (key, company_id) => {
  return `v1/PORTFOLIO_COMPANY_SETTINGS/${company_id}/${key}`;
};

export { setCache, getCache, removeCache, deleteOrphanCaches };
